/**
 * La direzione del movimento fra una schermata e l'altra.
 *
 * ── Perché non si può dedurre ─────────────────────────────────────────────
 *
 * Verrebbe da guardare l'indice della cronologia: se scende sei tornato
 * indietro. Qui non funziona, e per una ragione precisa: **i pulsanti indietro
 * dell'app non tornano indietro nella cronologia**, navigano in avanti verso la
 * schermata madre. Dal punto di vista del browser è sempre un passo avanti, e
 * la deduzione direbbe «avanti» anche mentre l'utente indietreggia.
 *
 * Cambiare quel comportamento — far fare al pulsante un `history.back()` vero —
 * romperebbe i percorsi in cui la madre non è da dove sei arrivato: salvata una
 * spesa, «indietro» deve portare al gruppo, non di nuovo al modulo.
 *
 * Quindi la direzione la **dichiarano le affordance**, che sono le uniche a
 * sapere cosa stanno facendo. Sono tre, non due:
 *
 * - **avanti** — si entra in qualcosa: la nuova schermata arriva da destra;
 * - **indietro** — il pulsante della testata: la vecchia esce verso destra;
 * - **laterale** — la barra in basso: le sezioni sono affiancate, non annidate,
 *   e farle scivolare direbbe una gerarchia che non esiste. Dissolvenza.
 *
 * ── Perché un attributo sul documento ─────────────────────────────────────
 *
 * Perché l'animazione la scrive il CSS su pseudo-elementi (`::view-transition-*`)
 * che non appartengono a nessun componente: non c'è nessun nodo React a cui
 * appendere una classe. L'attributo va scritto **prima** che parta la
 * navigazione, perché è in quel momento che il browser fotografa la pagina.
 */
export type NavDirection = "forward" | "back" | "lateral";

const ROOT_ATTRIBUTE = "nav";

function set(direction: NavDirection): void {
  if (typeof document === "undefined") return;

  document.documentElement.dataset[ROOT_ATTRIBUTE] = direction;
}

/** Il pulsante indietro della testata. */
export function markBack(): void {
  set("back");
}

/** La barra di navigazione in basso: sezioni affiancate, non annidate. */
export function markLateral(): void {
  set("lateral");
}

/**
 * Riporta la direzione ad «avanti» dopo ogni navigazione.
 *
 * Senza, un tocco su «indietro» lascerebbe l'attributo su `back` e la
 * navigazione **successiva** — magari un tocco su un gruppo — scivolerebbe al
 * contrario. «Avanti» è il valore giusto a riposo: è ciò che fa qualunque
 * tocco che non sia uno dei due casi speciali.
 */
export function resetDirectionAfterNavigation(subscribe: (fn: () => void) => () => void): () => void {
  return subscribe(() => set("forward"));
}
